import { scaleLinear, scaleTime } from "d3-scale"
import { line, curveLinear } from "d3-shape"
import { axisBottom } from "d3-axis"
import moment from "moment"
import { createSelector } from "reselect"
import { first, last, filter, get } from "lodash"
import { calculateHighlighter } from "../helpers/highlighter"
import { xSmallAxisTicks } from "../helpers/x-axis-ticks"

const MARGINS = { top: 0, left: 0, right: 92, bottom: 0 }

const getDate = props => props.date
const getRawData = props => props.data
const getWidth = props => props.width || 150
const getStartDate = props => props.startDate
const getEndDate = props => props.endDate
const getColor = props => props.color
const getHeight = props => props.height
const getBackgroundColor = props => props.background

const getRealWidth = createSelector(
  [getWidth],
  (width) => width - MARGINS.left - MARGINS.right,
)

export const getData = createSelector(
  [getRawData],
  data => filter(data, "value"),
)

const getMinX = createSelector(
  [getStartDate, getData],
  (startDate, data) => {
    if (data && data.length > 0) return moment(first(data).date).toDate()
    return moment(startDate).toDate()
  },
)

const getMaxX = createSelector(
  [getEndDate, getData],
  (endDate, data) => {
    if (data && data.length > 0) return moment(last(data).date).toDate()
    return moment(endDate).toDate()
  },
)

const getYValues = createSelector(
  [getData],
  (data) => data.map(datum => datum.value),
)

// [ToDo] implement scaling function for things other then percentages
export const getMinY = createSelector(
  [getYValues],
  (yValues) => Math.floor(Math.min(...yValues) * 100) / 100,
)

// [ToDo] implement scaling function for things other then percentages
export const getMaxY = createSelector(
  [getYValues],
  (yValues) => Math.ceil(Math.max(...yValues) * 100) / 100,
)

export const getScaleX = createSelector(
  [getMinX, getMaxX, getRealWidth],
  (minX, maxX, width) => scaleTime()
    .domain([minX, maxX])
    .rangeRound([0, width]),
)

export const getScaleY = createSelector(
  [getMinY, getMaxY, getHeight],
  (minY, maxY, height) => scaleLinear()
    .domain([minY, maxY])
    .rangeRound([height - MARGINS.top, MARGINS.bottom]),
)

export const getLine = createSelector(
  [getScaleX, getScaleY, getData, getColor],
  (scaleX, scaleY, data, color) => ({
    d: line()
      .x(d => scaleX(d.date))
      .y(d => scaleY(d.value))
      .curve(curveLinear)(data),
    transform: `translate(${MARGINS.left},${MARGINS.top - MARGINS.bottom})`,
    style: { stroke: color },
  }),
)

export const getClickableRect = createSelector(
  [getRealWidth, getScaleY, getMinY, getMaxY, getHeight],
  (width, scaleY, minY, maxY, height) => ({
    x: MARGINS.left,
    y: MARGINS.top,
    height: maxY === minY ? height : scaleY(minY),
    width,
  }),
)

export const getBackgroundRect = createSelector(
  [getClickableRect, getBackgroundColor],
  (rect, background) => ({
    ...rect,
    style: { fill: background },
  }),
)

const getMarkX = createSelector(
  [getRealWidth],
  (realWidth) => realWidth + 4,
)

export const getTopMark = createSelector(
  [getMarkX, getScaleY, getMaxY, getMinY],
  (markX, scaleY, maxY, minY) => ({
    x: markX,
    y: maxY === minY ? 0 : scaleY(maxY),
  }),
)

export const getBottomMark = createSelector(
  [getMarkX, getScaleY, getMaxY, getMinY, getHeight],
  (markX, scaleY, maxY, minY, height) => ({
    x: markX,
    y: maxY === minY ? height : scaleY(minY),
  }),
)

export const getCurrentValue = createSelector(
  [getData],
  (data) => get(last(data), "value"),
)

export const getCurrentMark = createSelector(
  [getMarkX, getHeight, getScaleY, getCurrentValue, getColor],
  (markX, height, scaleY, value, color) => {
    const maxHeight = height - MARGINS.bottom - 20
    const minHeight = 20 + MARGINS.top
    const y = Math.min(maxHeight, Math.max(minHeight, scaleY(value)))

    return {
      x: markX,
      y,
      style: { fill: color },
    }
  },
)

export const getHighlighter = createSelector(
  [getDate, getScaleX, getScaleY, getMinX, getMaxX, getMinY, getMaxY, getData],
  (date, scaleX, scaleY, minX, maxX, minY, maxY, data) => data && data.length > 1 ? calculateHighlighter(date, scaleX, scaleY, minX, maxX, minY, maxY) : null,
)

export const getXTicks = createSelector(
  [getMinX, getMaxX],
  xSmallAxisTicks,
)

export const getXAxis = createSelector(
  [getHeight, getWidth, getScaleX, getXTicks],
  (height, width, scaleX, [ticks, formatter]) => ({
    fn: axisBottom(scaleX)
      .ticks(ticks)
      .tickFormat(formatter)
      .tickSize(-6),
    transform: `translate(0,${height})`,
    baseline: {
      x1: MARGINS.left,
      x2: width - MARGINS.right,
      y1: height,
      y2: height,
    },
  }),
)
